/**
 * Price + description display rules shared by every booking surface, mirroring
 * the Openings booking page (openings-client PR #251):
 *
 * - A base price flagged `up` is a "starting at" price and renders as `$60+`.
 * - An option price is exact and never carries `+`, even when the parent
 *   service's base price is `up`.
 * - The option's description wins over the service's description.
 */

export interface PriceLike {
  price?: number | null;
  up?: boolean | null;
  option?: { price?: number | null } | null;
}

export interface DescriptionLike {
  description?: string | null;
  option?: { description?: string | null } | null;
}

export const formatMoney = (amount: number) =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;

const usableOptionPrice = (option: PriceLike["option"]) =>
  option && typeof option.price === "number" && option.price > 0
    ? option.price
    : null;

/**
 * True when the price that will be shown is the service's own base price and
 * that base price is a "starting at" price.
 */
export const hasStartingAtPrice = (service: PriceLike): boolean =>
  Boolean(service.up) && usableOptionPrice(service.option) === null;

/**
 * `$85` for a priced option, `$60+` for a starting-at base price, `$60` for an
 * exact base price, `null` when there is no usable price at all.
 */
export const formatServicePrice = (service: PriceLike): string | null => {
  const optionPrice = usableOptionPrice(service.option);
  const amount =
    optionPrice ??
    (typeof service.price === "number" && service.price > 0
      ? service.price
      : null);
  if (amount === null) return null;
  return `${formatMoney(amount)}${hasStartingAtPrice(service) ? "+" : ""}`;
};

/**
 * The base price only, ignoring any chosen option — for rows that represent
 * the service itself (e.g. the "Regular" row of the option picker).
 */
export const formatBasePrice = (service: PriceLike): string | null =>
  formatServicePrice({ price: service.price, up: service.up });

/**
 * The amount a line contributes to a total: the option price when priced,
 * else the base price, else 0. Pair with `hasStartingAtPrice` when deciding
 * whether the total itself is "starting at".
 */
export const getEffectivePrice = (service: PriceLike): number =>
  usableOptionPrice(service.option) ??
  (typeof service.price === "number" ? service.price : 0);

/** Option description wins over the service description; trimmed; `undefined` when empty. */
export const getServiceDescription = (
  service: DescriptionLike,
): string | undefined => {
  const optionDescription = service.option?.description?.trim();
  if (optionDescription) return optionDescription;
  const description = service.description?.trim();
  return description || undefined;
};
